/**
 *  Web Share Wrapper
 *    Simple web component to turn any link into a navigator.share button.
 *    Wrapping <web-share-wrapper></web-share-wrapper> around a HTML element
 *    will build a button that has the same class as the link you first set.
 *
 *  Usage:
 *    <web-share-wrapper
 *      share-title="Share Article"
 *      share-content="Short description about article"
 *      share-url="//url.com"
 *    >
 *      <a class="o-btn" href="//different-url.com.au">
 *        Your share link text or HMTL
 *      </a>
 *    </web-share-wrapper>
 *    share-title, share-url and 
 *
 *
 *  Version
 *    1.0.0 - 2022/07/08
 */

import { t } from '@process-creative/slate-theme-tools';

type ShareOptions = {
  title:string,
  text:string,
  url:string
}

const ATTR_TITLE = 'share-title';
const ATTR_SHARE_URL = 'share-url';
const ATTR_SHARE_TEXT = 'share-content';

const EVT_ON_SHARE_SUCCESS = 'onShareSuccess';
const EVT_ON_SHARE_ERROR = 'onShareError';

class WebShareWrapper extends HTMLElement {
  // Getters/Setters
  get title():string {
    return this.getAttribute(ATTR_TITLE) || 'Share';
  }

  set title(title:string) {
    if(title) this.setAttribute(ATTR_TITLE, title);
  }

  get firstAnchorEl():HTMLAnchorElement|null {
    const firstAnchor = this.children[0] as HTMLAnchorElement
    return firstAnchor || null;
  }

  get text():string {
    //Defined share text
    let text = this.getAttribute(ATTR_SHARE_TEXT);

    //Inner Text of first anchor if there's no defined title
    if(!text && this.firstAnchorEl) text = this.firstAnchorEl.innerText;

    return (text || document.title);
  }

  set text(text:string) {
    if(text) this.setAttribute(ATTR_SHARE_TEXT, text);
  }

  get url():string {
    //URL we set on the element
    const urlFromAttr = this.getAttribute(ATTR_SHARE_URL);
    if(urlFromAttr) {
      return urlFromAttr;
    }

    //Cannonical page URL
    const cannonicalUrl = document.querySelector<HTMLLinkElement>
      ('link[rel=canonical]')?.href;
    if(cannonicalUrl) {
      return cannonicalUrl;
    }

    //window.location fallback
    return window.location.href;
  }

  set url(shareUrl:string) {
    if(shareUrl) this.setAttribute(ATTR_SHARE_URL, shareUrl);
  }

  // Properties
  private webShareEnabled:Boolean = false;
  private button:HTMLButtonElement|null = null;

  constructor() {
    super();

    this.webShareEnabled = 'share' in navigator;
    this.initShareElements();
  }

  initShareElements() {

    if(this.webShareEnabled && this.firstAnchorEl) {
      //Create a button element and replace with content and classes from <a>
      this.button = document.createElement('button');
      this.button.innerHTML = this.firstAnchorEl?.innerHTML;

      this.firstAnchorEl.classList?.forEach(clazz => {
        this.button?.classList.add(clazz);
      });

      this.removeChildren();
      this.appendChild(this.button);

    } else if(this.firstAnchorEl) {
      //Fallback, update anchor link title. Copies to clipboard on click
      this.firstAnchorEl.title = t('general.controls.copy_to_clipboard');
    }
  }

  share = (event:Event) => {
    event.preventDefault();
    const shareOptions:ShareOptions = {
      title: this.title,
      text: this.text,
      url: this.url
    };
    navigator
      .share(shareOptions)
      .then(() => this.successfulShare(shareOptions))
      .catch(error => this.abortedShare(error, shareOptions));
  }

  shareFallback = (event:Event) => {
    //Triggered when navigator.share is not supported
    event.preventDefault();
    navigator.clipboard.writeText(this.url);
    this.onFallbackCopy();
  }

  onFallbackCopy() {
    // Show a notification to the user that something has happened.
    // You can this with custom styled notifications for your site
    if(!this.firstAnchorEl) return;
    const originalHTML = this.firstAnchorEl.innerHTML;
    this.firstAnchorEl.innerHTML = t('general.controls.copy_to_clipboard_success');
    setTimeout(() => {
      if(this.firstAnchorEl) this.firstAnchorEl.innerHTML = originalHTML;
    }, 3000);
  }

  connectedCallback() {
    //Fires when element is inserted into the DOM
    if(this.webShareEnabled) {
      this.addEventListener('click', this.share);
    } else {
      this.addEventListener('click', this.shareFallback);
    }
  }

  disconnectedCallback() {
    //Fires when element is removed from the DOM
    if(this.webShareEnabled) {
      this.removeEventListener('click', this.share);
    } else {
      this.removeEventListener('click', this.shareFallback);
    }
  }

  successfulShare(options:ShareOptions) {
    //Dispatches successful event on successful navigator.share
    //Listen to this event if you need to change behavior elsewhere
    const event = new CustomEvent(EVT_ON_SHARE_SUCCESS, { detail: options });
    this.dispatchEvent(event);
  }

  abortedShare(error:Error, options:ShareOptions) {
    const event = new CustomEvent(EVT_ON_SHARE_ERROR, {
      detail: { ...options, ...error }
    });
    this.dispatchEvent(event);
  }

  removeChildren() {
    while (this.firstChild) {
      this.removeChild(this.firstChild);
    }
  }
}

if ('customElements' in window && !customElements.get('web-share-wrapper')) {
  customElements.define('web-share-wrapper', WebShareWrapper);
}